import BackendService from 'BackendService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Contact from '../../../models/Contact';
import { InvestmentEntity } from '../../../models/InvestmentEntity';
import Investor from '../../../models/Investor';
import { buildCombinedChartData, buildMultiSeriesChartData } from './contactPortfolioChartUtils';
import type {
  FundBreakdownEntry,
  OverviewMetrics,
  PortfolioSummary,
  PortfolioTagsState,
} from './contactPortfolioTypes';

export function useContactPortfolioData(contactId: string | undefined) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState<string | undefined>();

  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [breakdown, setBreakdown] = useState<FundBreakdownEntry[]>([]);
  const [overviewMetrics, setOverviewMetrics] = useState<OverviewMetrics | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [selectedFundIds, setSelectedFundIds] = useState<Set<string>>(new Set());
  const [dataType, setDataType] = useState<'cumulative' | 'discrete'>('cumulative');
  const [combinedView, setCombinedView] = useState(true);

  const [investments, setInvestments] = useState<Investor[]>([]);
  const [investmentsLoading, setInvestmentsLoading] = useState(true);

  const [tags, setTags] = useState<PortfolioTagsState | null>(null);
  const [tagsLoading, setTagsLoading] = useState(true);

  const [entities, setEntities] = useState<InvestmentEntity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);

  useEffect(() => {
    if (!contactId) return;
    new BackendService()
      .getContact(contactId)
      .then(setContact)
      .catch((e) => setContactError(e?.message ?? 'Failed to load contact'))
      .finally(() => setContactLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    const bs = new BackendService();
    setSummaryLoading(true);
    Promise.all([
      bs.getContactPortfolioSummary(contactId),
      bs.getContactInvestmentOverviewMetrics(contactId),
    ])
      .then(([ps, m]) => {
        const s = ps?.portfolioSummary;
        const bd =
          (ps as { calculationDetails?: { fundBreakdown?: FundBreakdownEntry[] } })
            ?.calculationDetails?.fundBreakdown ?? [];
        setSummary(s ?? null);
        setBreakdown(bd);
        setSelectedFundIds(new Set(bd.map((b) => b.fundExternalId)));
        setOverviewMetrics({
          projectedRoePercent: m?.projectedRoePercent ?? null,
          projectedIrrPercent: m?.projectedIrrPercent ?? null,
        });
      })
      .catch(console.error)
      .finally(() => setSummaryLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    new BackendService()
      .getContactInvestments(contactId)
      .then((data) => setInvestments(data ?? []))
      .catch(console.error)
      .finally(() => setInvestmentsLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    new BackendService()
      .getContactPortfolioTags(contactId)
      .then((data) =>
        setTags({
          descriptive:
            (data as { tags?: { descriptive?: PortfolioTagsState['descriptive'] } })?.tags
              ?.descriptive ?? {},
          calculated:
            (data as { tags?: { calculated?: PortfolioTagsState['calculated'] } })?.tags
              ?.calculated ?? {},
        }),
      )
      .catch(console.error)
      .finally(() => setTagsLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return;
    new BackendService()
      .getInvestmentEntitiesForUser(contactId)
      .then((data) => setEntities(data ?? []))
      .catch(console.error)
      .finally(() => setEntitiesLoading(false));
  }, [contactId]);

  const portfolioInvestors = useMemo(
    () => investments.filter((inv) => inv.status === 'COMMITTED_INVESTOR'),
    [investments],
  );

  const breakdownByFundId = useMemo(
    () => new Map(breakdown.map((b) => [b.fundExternalId, b])),
    [breakdown],
  );

  const hasChartData = useMemo(
    () => breakdown.some((b) => b.scaledTimeSeries.length > 0),
    [breakdown],
  );

  const combinedData = useMemo(
    () => (hasChartData ? buildCombinedChartData(breakdown, selectedFundIds, dataType) : []),
    [breakdown, selectedFundIds, dataType, hasChartData],
  );

  const multiData = useMemo(
    () =>
      hasChartData
        ? buildMultiSeriesChartData(breakdown, selectedFundIds, dataType)
        : { data: [], keys: [] },
    [breakdown, selectedFundIds, dataType, hasChartData],
  );

  const toggleFundSelection = useCallback((fundExternalId: string) => {
    setSelectedFundIds((prev) => {
      const next = new Set(prev);
      if (next.has(fundExternalId)) next.delete(fundExternalId);
      else next.add(fundExternalId);
      return next;
    });
  }, []);

  return {
    contact,
    contactLoading,
    contactError,
    summary,
    breakdown,
    overviewMetrics,
    summaryLoading,
    selectedFundIds,
    dataType,
    setDataType,
    combinedView,
    setCombinedView,
    toggleFundSelection,
    investmentsLoading,
    portfolioInvestors,
    breakdownByFundId,
    hasChartData,
    combinedData,
    multiData,
    tags,
    tagsLoading,
    entities,
    entitiesLoading,
  };
}
